import type { ComputedRef, Ref } from 'vue'
import type { LinkNavTab } from '@shared/ui/menus'

import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { CUSTOMER_TABS } from './customer-tabs'
import { useCustomerPrivileges } from './customer-privileges.hook'

interface CustomerDetailMenuReturn {
  menuRef: Ref<HTMLElement | null>
  config: ComputedRef<LinkNavTab[]>
}

/** The legacy customer tab bar: same order and privileges. */
export const useCustomerDetailMenu = (): CustomerDetailMenuReturn => {
  const route = useRoute()
  const customerId = route.params.id as string
  const {
    canViewCustomers,
    canSimpleViewDisciplines,
    canViewOrders,
  } = useCustomerPrivileges()

  const menuRef = ref<HTMLElement | null>(null)

  const isTabVisible = (path: AppRoute): boolean => {
    switch (path) {
      case AppRoute.CustomersShow:
        return canViewCustomers.value || canSimpleViewDisciplines.value
      case AppRoute.CustomersSubscriptions:
      case AppRoute.CustomersOrders:
        return canViewOrders.value
      default:
        return canViewCustomers.value
    }
  }

  const config = computed<LinkNavTab[]>(() => CUSTOMER_TABS.map(({ path, label }) => {
    const to = resolveUrl(path, { id: customerId }) ?? ''

    return {
      text: label,
      to,
      selected: route.path === to,
      disabled: false,
      visible: isTabVisible(path),
    }
  }))

  // The bar scrolls sideways on narrow screens: centre the open tab once it is rendered.
  watch(menuRef, (menu) => {
    const tab = menu?.querySelector<HTMLElement>('[aria-current="page"]')

    if (menu && tab) {
      menu.scrollLeft = tab.offsetLeft - (menu.clientWidth - tab.offsetWidth) / 2
    }
  }, { flush: 'post' })

  return {
    menuRef,
    config,
  }
}
